import { asText, capitalize, statusColor, symbol, formatRate } from '../configUtils';

/*
 * Submission detail header — fill-in-the-blanks config.
 * See headerSpec.js for what each part means. In short: list the fields you
 * want under title / eyebrow / status / ids / meta and the engine formats them.
 */
export const submissionConfig = {
  module: 'submission',
  tabs: [],
  primaryActions: [
    { key: 'approve', label: 'Approve', icon: 'approve', tone: 'success' },
    { key: 'reject', label: 'Reject', icon: 'reject', tone: 'danger' },
    { key: 'resume', label: 'Resume', icon: 'resume', tone: 'primary' },
  ],
  moreActions: [],
  header: {
    title: ['firstName', 'lastName'],

    // "Client - Job Title" — the role this candidate was submitted to.
    eyebrow: ({ val, has }) => [clientName(val), val('jobTitle')]
      .filter(has).map(asText).join(' - '),

    clientName: ({ val }) => clientName(val),

    // Internal submission status, coloured by its type (positive/negative/…).
    status: ({ val, has }) => {
      const internal = val('internalSubmissionStatus') ?? {};
      const text = internal.internalSubmissionStatusText;
      if (!has(text)) return null;
      const byType = { positive: 'success', negative: 'error', neutral: 'default' };
      return { label: capitalize(text), color: byType[internal.internalSubmissionStatusType] ?? statusColor(text) };
    },

    ids: [
      { label: 'Submission ID', field: ['submissionId', 'clientSubmissionId'] },
      { label: 'MS Req ID',     field: ['mspReqId', 'mspreqId'] },
      { label: 'Client Req ID', field: ['jobClientRefrenceId', 'jobClientReferenceId', 'clientJobReferenceId'] },
      { label: 'Job ID',        field: ['jobRefrenceId', 'jobReferenceId'] },
    ],

    meta: [
      { icon: 'location',   field: 'jobLocation' },
      { icon: 'experience', field: ['relevantExperienceYears', 'workExperienceYears'], suffix: ' yrs' },
      { icon: 'employment', field: 'workAuthorisation' },
      { icon: 'rate',       prefix: 'Client rate: ',    compute: clientRate },
      { icon: 'rate',       prefix: 'Candidate rate: ', compute: candidateRate },
    ],

    timestamp: { label: 'Created Date', field: ['submittedAt', 'createdAt', 'createdDate'] },
  },
};

// Client name, falling back to the nested client.name shape.
const clientName = (val) => {
  const raw = val('clientName') ?? val('client.name');
  return raw;
};

// Client rate (clientBudget), falling back to the legacy candidateBudget shape.
function clientRate({ val }) {
  const cb = val('clientBudget') ?? val('candidateBudget') ?? {};
  return formatRate({
    start: cb.clientBudgetStart ?? cb.candidateBudgetStart,
    end: cb.clientBudgetEnd ?? cb.candidateBudgetEnd,
    sym: cb.clientBudgetCurrencySymbol ?? cb.candidateBudgetCurrencySymbol
      ?? symbol(cb.clientBudgetCurrency ?? cb.candidateBudgetCurrency),
    unit: cb.clientBudgetUnit ?? cb.candidateBudgetUnit ?? 'Hour',
  });
}

// Candidate rate — accepted rate first, then the proposed rate.
function candidateRate({ val }) {
  const accepted = val('candidateRate') ?? {};
  const proposed = val('proposedRate') ?? {};
  return formatRate({
    start: accepted.candidateAcceptedRate,
    sym: accepted.candidateAcceptedCurrencySymbol ?? symbol(accepted.candidateAcceptedCurrency),
    unit: accepted.candidateAcceptedPayUnit ?? 'Hour',
  }) || formatRate({
    start: proposed.candidateProposedRate,
    sym: proposed.candidateProposedRateCurrencySymbol ?? symbol(proposed.candidateProposedRateCurrency),
    unit: proposed.candidateProposedRateUnit ?? 'Hour',
  });
}

export default submissionConfig;
